import Input from "components/Input";
import { useFeedValues } from "lib/context/FeedContext";
import responsiveStyling, { responsiveStylingMax } from "lib/responsive";
import { PrimaryColorPalette, TextAndShapesPalette } from "lib/styles/colors";
import SearchIcon from "lib/svgs/Search";
import { useRouter } from "next/router";
import { setCookie } from "nookies";
import { ChangeEvent, KeyboardEvent, ReactElement, useState } from "react";
import styled from "styled-components";
import * as gtag from "lib/analytics/gtag";
import Button, { ButtonType } from "components/Button";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  ${responsiveStyling({
    ">1024": `
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    `,
  })};
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  height: 64px;
  width: 100%;
  padding: 21px 18px;
  border: 1px solid ${TextAndShapesPalette.INPUT_FIELD};
  background-color: ${PrimaryColorPalette.MAIN_6};
  border-radius: 8px;
`;

const ButtonContainer = styled.div`
  ${responsiveStylingMax({
    "<1024": `
    margin-top: 12px;
  `,
  })};
  ${responsiveStyling({
    ">1024": `
      margin-left: 12px;
    `,
  })};
`;

interface Props {
  handlePressEnter?: () => void;
}

const MAX_AGE = 10 * 60 * 1000; // 10 min

export function NewsSearchRow({ handlePressEnter }: Props): ReactElement {
  const router = useRouter();
  const { keyword: initialKeyword } = router.query;
  const { setFeedValues, getFeedValues } = useFeedValues();
  const hasParams = Boolean(initialKeyword);
  const { query } = getFeedValues();
  const [localQuery, setQuery] = useState<string>(query ?? "");

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const handleSearch = () => {
    if (hasParams && typeof window !== "undefined") {
      setCookie(null, "hasEditedNewsSearch", "true", {
        maxAge: MAX_AGE,
      });
      router.replace("/news", undefined);
    }
    gtag.event({
      action: "news_search_clicked",
      category: "engagement",
    });
    setFeedValues({
      query: localQuery,
    });
  };

  const handleSearchOnReturn = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!event.key || event.key !== "Enter") {
      return;
    }
    if (handlePressEnter) {
      handlePressEnter();
    } else {
      handleSearch();
    }
  };

  return (
    <Container>
      <SearchContainer>
        <SearchIcon />
        <Input
          h="64px"
          focusBorderColor="white.0"
          borderLeftWidth="0px"
          borderRightWidth="0px"
          borderTopLeftRadius="0px"
          borderTopRightRadius="0px"
          borderBottomLeftRadius="0px"
          borderBottomRightRadius="0px"
          type="newsSearch"
          placeholder="Search by keyword"
          onChange={handleQueryChange}
          onKeyUp={handleSearchOnReturn}
        />
      </SearchContainer>
      <ButtonContainer>
        <Button styleType={ButtonType.PRIMARY} onClick={handleSearch}>
          Search
        </Button>
      </ButtonContainer>
    </Container>
  );
}
